import type {
	ConstantsPublicTeamInviteResponse,
	ConstantsPublicTeamMemberResponse,
	ConstantsTeamSettingsStruct,
} from "@gosaas/types";
import { useQuery } from "@tanstack/react-query";
import { apiClient, requestWithHandling } from "@/utils/api";
import { reportQueryError } from "@/utils/query-errors";

export const useFetchTeamMembers = (
	teamID: string,
	enabled: boolean = true,
) => {
	const {
		data: teamMembers,
		isLoading,
		refetch: refetchTeamMembers,
	} = useQuery({
		queryKey: ["team-members", teamID],
		enabled: !!teamID && enabled,
		queryFn: async () => {
			try {
				const response = await requestWithHandling<
					ConstantsPublicTeamMemberResponse[]
				>(() => apiClient.api.teamMembersList(teamID));
				if (response.code === "SUCCESS") {
					return response.data;
				}

				throw new Error(response.message || "Failed to load team members");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load team members" });
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { teamMembers, isLoading, refetchTeamMembers };
};

export const useFetchTeamInvites = (teamID: string) => {
	const {
		data: teamInvites,
		isLoading: isLoadingInvites,
		refetch: refetchInvites,
	} = useQuery({
		queryKey: ["team-invites", teamID],
		queryFn: async () => {
			try {
				const response = await requestWithHandling<
					ConstantsPublicTeamInviteResponse[]
				>(() => apiClient.api.teamMembersInvitesList(teamID));
				if (response.code === "SUCCESS") {
					return response.data;
				}

				throw new Error(response.message || "Failed to load team invites");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load team invites" });
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { teamInvites, isLoadingInvites, refetchInvites };
};

export const useFetchTeamSettings = (teamID: string) => {
	const {
		data: teamSettings,
		isLoading: isLoadingSettings,
		refetch: refetchTeamSettings,
	} = useQuery({
		queryKey: ["team-settings", teamID],
		queryFn: async () => {
			try {
				const response = await requestWithHandling<ConstantsTeamSettingsStruct>(
					() => apiClient.api.teamSettingsList(teamID),
				);
				if (response.code === "SUCCESS") {
					return response.data;
				}

				throw new Error(response.message || "Failed to load team settings");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load team settings" });
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { teamSettings, isLoadingSettings, refetchTeamSettings };
};
